import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import { Cross } from '@sravni/react-icons';
import { useStyles, useMergedRef, useBoolean, preventBodyScroll } from '@sravni/react-utils';
import { useBackwardEvent } from '../../../hooks/useBackwardEvent.js';
import { Icon } from '../../Icon/src/Icon.js';
import { Portal } from '../../Portal/src/Portal.js';
import { EASINGS } from '../../Transition/src/_utils.js';
import '../../Transition/src/Fade.js';
import '../../Transition/src/ScaleFade.js';
import '../../Transition/src/SlideFade.js';
import '../../Transition/src/Collapse.js';
import modules_0b64ef5e from '../styles/Sheet.module.less.js';
import SheetContext from './SheetContext.js';

const defaultDragElastic = { top: 0.5, bottom: 0.5 };
/**
 * @description Sheet component.
 *
 * @component
 * @example
 * ```jsx
 * <Sheet>content</Sheet>
 * ```
 */
const Sheet = React.forwardRef((props, ref) => {
    const { visible, closable, fullscreen, closeOnBackward = false, className, children, onClose, ...restProps } = props;
    const cx = useStyles(modules_0b64ef5e);
    const sheetRef = React.useRef(null);
    const isUnmountEvent = React.useRef(true);
    const referenceRef = useMergedRef(sheetRef, ref);
    const [dragElastic, setDragElastic] = React.useState(defaultDragElastic);
    // Auxiliary transparent layer helps to disable user interaction while animation is in progress
    const [isLayerEnabled, setIsLayerEnabled] = useBoolean(false);
    const sheetContextProviderValue = React.useMemo(() => ({ setDragElastic }), []);
    const handleClose = React.useCallback((event) => {
        preventBodyScroll();
        onClose?.(event);
        isUnmountEvent.current = false;
    }, [onClose]);
    const handleDragEnd = React.useCallback((event, { velocity }) => {
        const sheetEl = sheetRef.current;
        if (!sheetEl)
            return;
        const contentHeight = sheetEl.getBoundingClientRect().height;
        // Close if dragged over 60%
        if (velocity.y / contentHeight > 0.6) {
            handleClose(event);
        }
    }, [sheetRef, handleClose]);
    const handlePreventBodyScroll = React.useCallback(() => {
        if (visible && isUnmountEvent.current) {
            preventBodyScroll();
        }
    }, [visible, isUnmountEvent]);
    const sheetMotionProps = React.useMemo(() => ({
        initial: 'exit',
        exit: 'exit',
        variants: {
            enter: { y: 0, transition: { duration: 0.2, type: 'easeOut' } },
            exit: {
                y: sheetRef.current ? sheetRef.current.getBoundingClientRect().height : 1000,
                transition: { duration: 0.2, type: 'easeOut' },
            },
        },
        animate: visible ? 'enter' : 'exit',
    }), [visible, sheetRef]);
    const backdropMotionProps = React.useMemo(() => ({
        variants: {
            exit: {
                opacity: 0,
                transition: {
                    duration: 0.2,
                    delay: 0.2,
                    ease: EASINGS.easeOut,
                },
            },
            enter: {
                opacity: 0.6,
                transition: {
                    duration: 0.2,
                    delay: 0.1,
                    ease: EASINGS.easeIn,
                },
            },
        },
        animate: visible ? 'enter' : 'exit',
        initial: 'exit',
        exit: 'exit',
    }), [visible]);
    const dragProps = React.useMemo(() => ({
        drag: 'y',
        dragElastic,
        dragConstraints: { top: 0, bottom: 0 },
        dragMomentum: false,
        onDragEnd: handleDragEnd,
    }), [dragElastic, handleDragEnd]);
    const handlePointerDown = React.useCallback(() => setDragElastic(defaultDragElastic), []);
    React.useEffect(() => {
        if (visible) {
            preventBodyScroll(true);
            isUnmountEvent.current = true;
        }
        return handlePreventBodyScroll;
    }, [handlePreventBodyScroll, visible]);
    const { closeEventHandler } = useBackwardEvent(closeOnBackward, !!visible, handleClose);
    return (React.createElement(Portal, null,
        isLayerEnabled && React.createElement("div", { className: cx('auxiliary-layer') }),
        React.createElement(AnimatePresence, { onExitComplete: closeEventHandler }, visible && (React.createElement("div", { className: cx('overlay') },
            React.createElement(motion.div, { ...backdropMotionProps, className: cx('backdrop'), onClick: handleClose }),
            React.createElement(motion.div, { "data-qa": "Sheet", ...dragProps, ...sheetMotionProps, ...restProps, ref: referenceRef, className: cx(className, 'container', { fullscreen }), onPointerDown: handlePointerDown, onAnimationStart: setIsLayerEnabled.on, onAnimationComplete: setIsLayerEnabled.off },
                closable && React.createElement(Icon, { className: cx('close'), icon: React.createElement(Cross, null), onClick: handleClose }),
                React.createElement(SheetContext.Provider, { value: sheetContextProviderValue }, children)))))));
});
Sheet.defaultProps = {
    closeOnBackward: false,
};
Sheet.displayName = 'Sheet';

export { Sheet };
//# sourceMappingURL=Sheet.js.map
